import renderer from "./renderer";
import { respondToRailsRerender } from "./rerenderEvent";

/**
 * @typedef {Object} ExportedRailsReturn
 * @property {Promise<void>[]} renderedComponentList - Array of promises that resolve once all elements have been rendered
 * @property {GenEventResponderCleanup} responseListener - An optional signature object that will be compared to the event.detail, based on the `signatureParams` passed to the generator
 */

/**
 * Enables entrypoint to register components that are placed via server side rendering.
 *
 * Note that exported components can take two different forms:
 *
 * ```
 * import StaticComponent from "Component"
 *
 * exportComponentsToRails(
 *   "entrypoint",
 *   {
 *     "static-import" : StaticComponent,
 *     "dynamic-import" : import("Component")
 *   }
 * )
 * ```
 *
 * @see `app/helpers/react_helper.rb`
 * @param {string} entrypoint Basename of the entrypoint i.e. "entrypoint" for `client/entries/entrypoint.js`
 * @param exported - hash list of elements to pass to the renderer
 * @param [componentRenderer] - method that will return an array of Promise wrapped components
 * @return {ExportedRailsReturn}
 */
const exportComponentsToRails = (
  entrypoint,
  exported = {},
  { componentRenderer = renderer } = {}
) => {
  const renderComponents = () => componentRenderer(entrypoint, exported);

  const responseListener = respondToRailsRerender(renderComponents);

  const renderedComponentList = renderComponents();

  if (!Array.isArray(renderedComponentList)) {
    throw new Error("Renderer did not return a map of components!");
  }

  return { renderedComponentList, responseListener };
};

export default exportComponentsToRails;
